import { Outlet, NavLink } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  ShoppingBag, 
  Package, 
  Users, 
  Wrench, 
  CreditCard, 
  FileText,
  Menu,
  X,
  LogOut,
  Search,
  Settings,
  HandCoins,
  Wallet,
  Headphones,
  Smartphone,
  StickyNote,
  Sun,
  Moon, Building2
} from 'lucide-react';
import { Button } from './ui/button';
import SmartSearch from './SmartSearch';
import NotificationsBell from './NotificationsBell';

export default function Layout({ user, onLogout }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('app_theme') || 'dark');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('app_theme', theme);
  }, [theme]);

  const isAdmin = user.role === 'admin';
  const perms = user.permissions?.pages || {};
  const hasPerm = (key) => isAdmin || perms[key] === true;

  const navItems = [
    ...(isAdmin ? [{ name: 'لوحة التحكم', path: '/', icon: LayoutDashboard, testId: 'nav-dashboard', permKey: 'dashboard' }] : []),
    { name: 'المبيعات', path: '/sales', icon: ShoppingCart, testId: 'nav-sales', permKey: 'sales' },
    { name: 'المشتريات', path: '/purchases', icon: ShoppingBag, testId: 'nav-purchases', permKey: 'purchases' },
    { name: 'المخزن', path: '/inventory', icon: Package, testId: 'nav-inventory', permKey: 'inventory' },
    { name: 'العملاء', path: '/customers', icon: Users, testId: 'nav-customers', permKey: 'customers' },
    { name: 'الصيانة', path: '/repairs', icon: Wrench, testId: 'nav-repairs', permKey: 'repairs' },
    { name: 'التقسيط', path: '/installments', icon: CreditCard, testId: 'nav-installments', permKey: 'installments' },
    { name: 'الذمم', path: '/debts', icon: HandCoins, testId: 'nav-debts', permKey: 'debts' },
    { name: 'الموردين', path: '/suppliers', icon: Building2, testId: 'nav-suppliers', permKey: 'debts' },
    { name: 'الصندوق', path: '/cash-box', icon: Wallet, testId: 'nav-cashbox', permKey: 'cashbox' },
    { name: 'الاكسسوارات', path: '/accessories', icon: Headphones, testId: 'nav-accessories', permKey: 'accessories' },
    { name: 'الأندرويد', path: '/android', icon: Smartphone, testId: 'nav-android', permKey: 'android' },
    { name: 'الملاحظات', path: '/notes', icon: StickyNote, testId: 'nav-notes', permKey: null },
    { name: 'التقارير', path: '/reports', icon: FileText, testId: 'nav-reports', permKey: 'reports' },
    ...(isAdmin ? [{ name: 'الإعدادات', path: '/settings', icon: Settings, testId: 'nav-settings', permKey: 'settings' }] : []),
  ].filter(item => item.permKey === null || hasPerm(item.permKey));

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Mobile Sidebar Backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 right-0 h-full w-64 bg-[#18181b] border-l border-zinc-800 z-50
        transform transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}
      `}>
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-4 border-b border-zinc-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-11 h-11 rounded-full bg-white p-0.5 ring-1 ring-zinc-700 overflow-hidden flex-shrink-0">
                  <img
                    src="/logo192.png"
                    alt="مركز مصطفى سنتر"
                    className="w-full h-full object-contain rounded-full"
                  />
                </div>
                <h1 className="text-base font-bold text-white truncate">مركز مصطفى سنتر</h1>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-zinc-400 hover:text-white flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-zinc-400 mt-2 pr-1">{user.name}</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                data-testid={item.testId}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-all duration-200
                  ${isActive 
                    ? 'bg-[#007aff] text-white' 
                    : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }
                `}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
              </NavLink>
            ))}
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-zinc-800">
            <Button
              onClick={onLogout}
              variant="ghost"
              className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-950/20"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 ml-3" />
              تسجيل الخروج
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:mr-64">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-zinc-950/70 backdrop-blur-xl border-b border-zinc-800">
          <div className="flex items-center justify-between p-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-white"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>

            <div className="flex-1 max-w-xl mr-4">
              <SmartSearch placeholder="البحث الذكي بـ IMEI، الموديل، العميل، رقم الفاتورة..." />
            </div>

            <div className="flex items-center gap-2">
              <NotificationsBell />
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium text-white">{user.name}</p>
                <p className="text-xs text-zinc-400">{user.role === 'admin' ? 'مدير' : 'موظف'}</p>
              </div>
              <Button
                onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
                variant="ghost"
                size="sm"
                className="text-zinc-300 hover:text-white hover:bg-zinc-800 gap-1.5"
                data-testid="theme-toggle-button"
                title={theme === 'dark' ? 'الوضع النهاري' : 'الوضع الليلي'}
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                <span className="hidden md:inline">{theme === 'dark' ? 'نهاري' : 'ليلي'}</span>
              </Button>
              <Button
                onClick={onLogout}
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-300 hover:bg-red-950/20 gap-1.5"
                data-testid="logout-button-header"
                title="تسجيل الخروج"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">خروج</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
